import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {Map<number, { title: string, canonical: string, previous: string, next: string }>} */
  #pageMetadata = new Map();

  /** @type {number | null} */
  #scrollUpdateFrame = null;

  /** @type {EventTarget[]} */
  #scrollContainers = [];

  /** @type {boolean} */
  #isRestoringHistory = false;

  /** @type {boolean} */
  #hasUserScrollIntent = false;

  /** @type {number | null} */
  #touchStartY = null;

  /** @type {string} */
  #paginationQuery = '';

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    if (this.getAttribute('infinite-scroll') === 'true') {
      window.addEventListener('wheel', this.#handleUserScrollIntent, { passive: true });
      window.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      window.addEventListener('touchmove', this.#handleUserScrollIntent, { passive: true });
      window.addEventListener('keydown', this.#handleUserScrollIntent);
    }

    if (this.getAttribute('infinite-scroll') === 'true') {
      const initialPage = Number(this.refs.cards?.[0]?.dataset.page ?? new URL(window.location.href).searchParams.get('page') ?? 1);
      this.#paginationQuery = this.#getPaginationQuery();
      this.#storePageMetadata(this.querySelector('[data-pagination-page-meta]'));
      this.#initializeHistory(initialPage);
      this.#scrollContainers = [
        document.querySelector('.page-wrapper'),
        document.scrollingElement,
        document,
      ].filter((container) => container instanceof EventTarget);
      for (const container of this.#scrollContainers) {
        container.addEventListener('scroll', this.#handleScroll, { passive: true });
      }
      window.addEventListener('popstate', this.#handlePopState);
      window.addEventListener('pageshow', this.#handlePageShow);
      this.#applyPageMetadata(initialPage);
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    for (const container of this.#scrollContainers) {
      container.removeEventListener('scroll', this.#handleScroll);
    }
    this.#scrollContainers = [];
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('wheel', this.#handleUserScrollIntent);
    window.removeEventListener('touchstart', this.#handleTouchStart);
    window.removeEventListener('touchmove', this.#handleUserScrollIntent);
    window.removeEventListener('keydown', this.#handleUserScrollIntent);
    if (this.#scrollUpdateFrame !== null) {
      cancelAnimationFrame(this.#scrollUpdateFrame);
      this.#scrollUpdateFrame = null;
    }
  }

  #initializeHistory(page) {
    if (!Number.isInteger(page) || page < 1) return;

    const currentState = history.state ?? {};
    const state = {
      ...currentState,
      paginationListSection: this.sectionId,
      paginationPage: page,
      paginationScrollTop: getScrollTop(),
    };

    history.replaceState(state, '', window.location.href);
  }

  #getPaginationQuery() {
    const params = new URL(window.location.href).searchParams;
    params.delete('page');
    return params.toString();
  }

  #storePageMetadata(source) {
    if (!source) return;

    let metadataElement = source;
    if (typeof source === 'string') {
      const parsedPage = new DOMParser().parseFromString(source, 'text/html');
      metadataElement = parsedPage.querySelector('[data-pagination-page-meta]');
    }
    if (!metadataElement) return;

    const page = Number(metadataElement.dataset.page);
    const title = metadataElement.dataset.title;
    const canonical = metadataElement.dataset.canonical;
    if (!Number.isInteger(page) || page < 1 || !title || !canonical) return;

    this.#pageMetadata.set(page, {
      title,
      canonical,
      previous: metadataElement.dataset.prev ?? '',
      next: metadataElement.dataset.next ?? '',
    });
  }

  #applyPageMetadata(page) {
    const metadata = this.#pageMetadata.get(page);
    if (!metadata) return false;

    document.title = metadata.title;

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', metadata.canonical);

    this.#setHeadLink('prev', metadata.previous);
    this.#setHeadLink('next', metadata.next);
    this.#syncPaginationLink('previous', metadata.previous);
    this.#syncPaginationLink('next', metadata.next);
    this.#syncPaginationCurrentPage(page);
    this.#setMetaContent('meta[property="og:url"]', metadata.canonical);
    this.#setMetaContent('meta[property="og:title"]', metadata.title);
    this.#setMetaContent('meta[name="twitter:title"]', metadata.title);

    return true;
  }

  #setHeadLink(rel, href) {
    let link = document.head.querySelector(`link[rel="${rel}"]`);
    if (!href) {
      link?.remove();
      return;
    }

    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.append(link);
    }

    link.setAttribute('href', new URL(href, window.location.origin).href);
  }

  #setMetaContent(selector, content) {
    const meta = document.head.querySelector(selector);
    if (meta) meta.setAttribute('content', content);
  }

  #syncPaginationCurrentPage(page) {
    const nav = this.querySelector('.pagination[data-current_page]');
    if (!nav) return;

    nav.setAttribute('data-current_page', page.toString());

    for (const item of nav.querySelectorAll('[data-pagination-page]')) {
      const pageNumber = Number(item.dataset.paginationPage);
      const pageControl = item.querySelector('.pagination__link--page');
      if (!Number.isInteger(pageNumber) || !pageControl) continue;

      if (pageNumber === page) {
        if (pageControl.tagName !== 'SPAN') {
          const currentPage = document.createElement('span');
          for (const attribute of pageControl.attributes) {
            if (attribute.name !== 'href' && attribute.name !== 'on:click') {
              currentPage.setAttribute(attribute.name, attribute.value);
            }
          }
          currentPage.innerHTML = pageControl.innerHTML;
          currentPage.classList.add('pagination__link--current');
          currentPage.setAttribute('aria-current', 'page');
          pageControl.replaceWith(currentPage);
        }
        continue;
      }

      const url = new URL(window.location.href);
      if (pageNumber === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', pageNumber.toString());
      }
      url.hash = '';

      if (pageControl.tagName !== 'A') {
        const pageLink = document.createElement('a');
        for (const attribute of pageControl.attributes) {
          if (attribute.name !== 'aria-current' && attribute.name !== 'on:click') {
            pageLink.setAttribute(attribute.name, attribute.value);
          }
        }
        pageLink.classList.remove('pagination__link--current');
        pageLink.href = url.toString();
        pageLink.innerHTML = pageControl.innerHTML;
        pageControl.replaceWith(pageLink);
      } else {
        pageControl.href = url.toString();
      }
    }
  }

  #handleTouchStart = (event) => {
    this.#touchStartY = event.touches[0]?.clientY ?? null;
  };

  #handleUserScrollIntent = (event) => {
    if (this.getAttribute('infinite-scroll') !== 'true' || this.#hasUserScrollIntent) return;

    let isScrollingUp = false;
    if (event.type === 'wheel') {
      isScrollingUp = event.deltaY < 0;
    } else if (event.type === 'touchmove') {
      const currentY = event.touches[0]?.clientY;
      isScrollingUp = typeof currentY === 'number' && this.#touchStartY !== null && currentY > this.#touchStartY;
      this.#touchStartY = currentY ?? null;
    } else {
      isScrollingUp = ['ArrowUp', 'PageUp', 'Home'].includes(event.key);
    }

    if (!isScrollingUp) return;
    this.#hasUserScrollIntent = true;
    this.#observeViewMore();
  };

  #syncPaginationLink(direction, href) {
    const current = this.querySelector(`[data-pagination-direction="${direction}"]`);
    if (!current) return;

    if (href) {
      const link = current.tagName === 'A' ? current : document.createElement('a');
      if (link !== current) {
        for (const attribute of current.attributes) {
          link.setAttribute(attribute.name, attribute.value);
        }
        link.innerHTML = current.innerHTML;
        current.replaceWith(link);
      }
      link.setAttribute('href', new URL(href, window.location.origin).href);
      link.removeAttribute('aria-disabled');
      link.classList.remove('pagination__link--disabled');
      return;
    }

    if (current.tagName === 'A') {
      const disabled = document.createElement('span');
      for (const attribute of current.attributes) {
        if (attribute.name !== 'href') disabled.setAttribute(attribute.name, attribute.value);
      }
      disabled.innerHTML = current.innerHTML;
      disabled.setAttribute('aria-disabled', 'true');
      disabled.classList.add('pagination__link--disabled');
      current.replaceWith(disabled);
    }
  }

  #getVisiblePage() {
    const cards = this.querySelectorAll('[ref="cards[]"]');
    if (!cards.length) return null;

    const readingLine = Math.min(window.innerHeight * 0.4, 360);
    let firstCardBelowLine = null;
    let lastCardAtReadingLine = null;

    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (rect.bottom <= readingLine) continue;
      if (rect.top <= readingLine) {
        lastCardAtReadingLine = card;
        continue;
      }
      firstCardBelowLine ??= card;
      break;
    }

    const targetCard = lastCardAtReadingLine ?? firstCardBelowLine ?? cards[cards.length - 1];
    const page = Number(targetCard.dataset.page);
    return Number.isInteger(page) && page > 0 ? page : null;
  }

  #handleScroll = () => {
    if (this.#isRestoringHistory || this.#scrollUpdateFrame !== null) return;

    this.#scrollUpdateFrame = requestAnimationFrame(() => {
      this.#scrollUpdateFrame = null;
      if (this.#isRestoringHistory) return;

      const visiblePage = this.#getVisiblePage();
      if (!visiblePage || !this.#pageMetadata.has(visiblePage)) return;

      const currentState = history.state ?? {};
      const currentPage = Number(currentState.paginationPage);
      if (currentState.paginationListSection !== this.sectionId || !Number.isInteger(currentPage)) {
        this.#initializeHistory(visiblePage);
        this.#applyPageMetadata(visiblePage);
        return;
      }

      if (visiblePage !== currentPage) {
        // Preserve the last scroll position that still belonged to the old page.
        history.replaceState(
          {
            ...currentState,
            paginationScrollTop: Number(currentState.paginationScrollTop ?? getScrollTop()),
          },
          '',
          window.location.href
        );

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('page', visiblePage.toString());
        nextUrl.hash = '';
        history.pushState(
          {
            ...currentState,
            paginationListSection: this.sectionId,
            paginationPage: visiblePage,
            paginationScrollTop: getScrollTop(),
          },
          '',
          nextUrl
        );
        this.#applyPageMetadata(visiblePage);
        return;
      }

      history.replaceState(
        {
          ...currentState,
          paginationScrollTop: getScrollTop(),
        },
        '',
        window.location.href
      );
    });
  };

  #handlePageShow = (event) => {
    if (!event.persisted) return;

    const page = Number(new URL(window.location.href).searchParams.get('page') ?? 1);
    const renderedPages = [...this.querySelectorAll('[ref="cards[]"]')]
      .map((card) => Number(card.dataset.page))
      .filter((renderedPage) => Number.isInteger(renderedPage) && renderedPage > 0);

    // A back/forward cache restore can bypass popstate while retaining later pages.
    if (!renderedPages.includes(page) || renderedPages.some((renderedPage) => renderedPage > page)) {
      window.location.reload();
    }
  };

  #handlePopState = (event) => {
    if (this.#getPaginationQuery() !== this.#paginationQuery) {
      // Filter/sort/grid history changes need server-rendered HTML, not only a URL update.
      window.location.reload();
      return;
    }

    const state = event.state;
    if (state?.paginationListSection !== this.sectionId) return;

    const page = Number(state.paginationPage);
    if (!Number.isInteger(page) || page < 1) return;

    const renderedPages = [...this.querySelectorAll('[ref="cards[]"]')]
      .map((card) => Number(card.dataset.page))
      .filter((renderedPage) => Number.isInteger(renderedPage) && renderedPage > 0);

    // Infinite scroll keeps later pages in the grid; reload when this history
    // entry cannot be represented by the currently rendered cards.
    if (!renderedPages.includes(page) || renderedPages.some((renderedPage) => renderedPage > page)) {
      window.location.reload();
      return;
    }

    if (!this.#applyPageMetadata(page)) return;

    this.#isRestoringHistory = true;
    scrollTo({
      top: Number(state.paginationScrollTop ?? 0),
      behavior: 'instant',
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const visiblePage = this.#getVisiblePage();
        if (visiblePage && visiblePage !== page && this.#pageMetadata.has(visiblePage)) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', visiblePage.toString());
          const currentState = history.state ?? state;
          history.replaceState(
            {
              ...currentState,
              paginationListSection: this.sectionId,
              paginationPage: visiblePage,
              paginationScrollTop: getScrollTop(),
            },
            '',
            url
          );
          this.#applyPageMetadata(visiblePage);
        }
        this.#isRestoringHistory = false;
      });
    });
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious && (this.getAttribute('infinite-scroll') !== 'true' || this.#hasUserScrollIntent)) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
    this.#storePageMetadata(pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    // Infinite scroll extends the rendered list without navigating the document.
    // Keep its URL aligned with the server-rendered title and canonical.

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    // Reconcile a direct page load after the previous-page sentinel hydrates.
    const visiblePage = this.#getVisiblePage();
    if (visiblePage && this.#pageMetadata.has(visiblePage)) {
      const url = new URL(window.location.href);
      const currentUrlPage = Number(url.searchParams.get('page') ?? 1);
      const currentState = history.state ?? {};
      if (
        currentUrlPage !== visiblePage ||
        Number(currentState.paginationPage) !== visiblePage ||
        currentState.paginationListSection !== this.sectionId
      ) {
        url.searchParams.set('page', visiblePage.toString());
        history.replaceState(
          {
            ...currentState,
            paginationListSection: this.sectionId,
            paginationPage: visiblePage,
            paginationScrollTop: getScrollTop(),
          },
          '',
          url
        );
      }
      this.#applyPageMetadata(visiblePage);
    }

    // Infinite scroll extends the rendered list without navigating the document.
    // Keep its URL aligned with the server-rendered title and canonical.

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#paginationQuery = this.#getPaginationQuery();
    this.pages.clear();
    this.#pageMetadata.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((records) => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const gridChanged = records.some((record) => record.type === 'childList');

      if (newLastPage !== currentLastPage || gridChanged) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#paginationQuery = this.#getPaginationQuery();
        this.#storePageMetadata(this.querySelector('[data-pagination-page-meta]'));
        const currentPage = this.#getVisiblePage();
        if (currentPage) {
          this.#initializeHistory(currentPage);
          this.#applyPageMetadata(currentPage);
        }
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
